import React from 'react';
import { CheckCircle2, XCircle, BookOpen, Activity, Trophy, Target, ClipboardList, HeartHandshake, Users, GraduationCap, Star, Download, FileText, MessageCircle, PlayCircle, ShieldCheck, ArrowRight } from 'lucide-react';
import { CountdownBar } from './components/CountdownBar';
import { PurchasePopup } from './components/PurchasePopup';
import { MetaPixel } from './components/MetaPixel';
import { TomosAccordion } from './components/TomosAccordion';
import { BonusesAccordion } from './components/BonusesAccordion';
const CHECKOUT_URL = 'https://checkout.ebookbtnlines.shop/cart/45399610327087:1';
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/gytRFbFiLhRv5f61Ah7RuE/Foto_tarjetas_RUGBY.png';
const scrollToBuy = (e?: React.MouseEvent) => {
  e?.preventDefault();
  const el = document.getElementById('comprar');
  if (el) el.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
};
const CTAButton = ({
  className = '',
  text = 'QUIERO RUGBY INFANTIL 360',
  variant = 'scroll'
}: {
  className?: string;
  text?: string;
  variant?: 'scroll' | 'buy';
}) => {
  const baseClass = `inline-flex items-center justify-center gap-2 bg-rugby-blue hover:bg-rugby-light text-white font-display text-2xl md:text-3xl py-4 px-8 rounded-xl shadow-[0_0_30px_rgba(37,99,235,0.4)] hover:shadow-[0_0_40px_rgba(37,99,235,0.6)] transition-all transform hover:scale-105 uppercase tracking-wide ${className}`;
  if (variant === 'buy') {
    return <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className={baseClass} data-id="element-0">
        {text}
        <ArrowRight className="w-6 h-6 md:w-8 md:h-8" data-id="element-1" />
      </a>;
  }
  return <a href="#comprar" onClick={scrollToBuy} className={baseClass} data-id="element-2">
      {text}
      <ArrowRight className="w-6 h-6 md:w-8 md:h-8" data-id="element-3" />
    </a>;
};
export function App() {
  return <div className="min-h-screen bg-navy-800 text-white font-sans selection:bg-rugby-blue selection:text-white" data-id="element-4">
      <MetaPixel data-id="element-5" />
      <CountdownBar data-id="element-6" />
      <PurchasePopup data-id="element-7" />

      {/* HERO SECTION */}
      <section className="pt-16 pb-20 px-4 relative overflow-hidden" data-id="element-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(37,99,235,0.15)_0%,transparent_70%)] pointer-events-none" data-id="element-9"></div>
        <div className="max-w-5xl mx-auto text-center relative z-10" data-id="element-10">
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.9] mb-4 uppercase" data-id="element-11">
            DEJÁ DE{' '}
            <span className="text-rugby-blue relative inline-block" data-id="element-12">
              PERDER HORAS
              <svg className="absolute w-full h-4 -bottom-2 left-0 text-rugby-blue opacity-80" viewBox="0 0 100 20" preserveAspectRatio="none" data-id="element-13">
                <path d="M0 10 Q 50 20 100 10" stroke="currentColor" strokeWidth="4" fill="none" data-id="element-14" />
              </svg>
            </span>
            <br data-id="element-15" />
            PLANIFICANDO TUS CLASES
          </h1>

          <div className="relative max-w-4xl mx-auto -my-6 md:-my-10" data-id="element-16">
            <div className="absolute inset-0 bg-rugby-blue/20 blur-[100px] rounded-full" data-id="element-17"></div>
            <a href="#comprar" onClick={scrollToBuy} className="block relative z-10 hover:scale-[1.02] transition-transform duration-500 cursor-pointer" data-id="element-18">
              <img src={PRODUCT_IMAGE} alt="Rugby Infantil 360 - Sistema Completo" className="w-full h-auto drop-shadow-2xl" data-id="element-19" />
            </a>
          </div>

          <p className="text-lg md:text-2xl text-slate-300 mb-10 max-w-3xl mx-auto font-medium" data-id="element-20">
            Más de{' '}
            <span className="text-white font-bold" data-id="element-21">
              300 ejercicios, juegos y clases listas para usar
            </span>{' '}
            para entrenadores y profes de rugby infantil.
          </p>

          <div className="mb-12" data-id="element-22">
            <CTAButton text="QUIERO MIS CLASES LISTAS" data-id="element-23" />
            <p className="mt-4 text-sm text-slate-400 flex items-center justify-center gap-2" data-id="element-24">
              <ShieldCheck className="w-4 h-4 text-green-400" data-id="element-25" />
              Pago 100% seguro. Acceso inmediato.
            </p>
          </div>
        </div>
      </section>

      {/* PAIN POINTS SECTION */}
      <section className="py-14 px-4 bg-navy-900 border-y border-navy-700" data-id="element-26">
        <div className="max-w-4xl mx-auto" data-id="element-27">
          <h2 className="font-display text-4xl md:text-5xl text-center mb-8 uppercase" data-id="element-28">
            ¿Te suena <span className="text-red-500" data-id="element-29">familiar</span> esta
            situación?
          </h2>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-30">
            {['Llegás a la cancha sin saber exactamente qué ejercicios dar hoy.', 'Los chicos se aburren rápido o se dispersan porque las actividades no son dinámicas.', 'Perdés horas en tu casa intentando armar planificaciones que después no funcionan.', 'Sentís que tus jugadores no avanzan ni aprenden las destrezas básicas del rugby.', 'Repetís siempre los mismos 3 o 4 juegos porque no se te ocurren ideas nuevas.', 'Te frustra no tener una estructura profesional en tu escuelita.'].map((pain, i) => <div key={i} className="flex items-start gap-4 bg-navy-800 p-6 rounded-2xl border border-navy-700" data-id="element-31">
                <XCircle className="w-8 h-8 text-red-500 flex-shrink-0 mt-1" data-id="element-32" />
                <p className="text-slate-300 text-lg leading-relaxed" data-id="element-33">{pain}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* SOLUTION INTRO + ÍNDICE DESPLEGABLE */}
      <section className="py-14 px-4 relative" data-id="element-34">
        <div className="max-w-4xl mx-auto text-center mb-10" data-id="element-35">
          <h2 className="font-display text-5xl md:text-6xl mb-4 uppercase" data-id="element-36">
            LA SOLUCIÓN DEFINITIVA:
            <br data-id="element-37" />
            <span className="text-rugby-blue" data-id="element-38">RUGBY INFANTIL 360</span>
          </h2>
          <p className="text-base md:text-lg text-slate-400 uppercase tracking-widest font-bold" data-id="element-39">
            Mirá todo lo que vas a encontrar dentro de los 6 tomos
          </p>
        </div>

        <TomosAccordion data-id="element-40" />

        <div className="text-center mt-10" data-id="element-41">
          <CTAButton text="SÍ, QUIERO EL SISTEMA COMPLETO" data-id="element-42" />
        </div>
      </section>

      {/* WHO IS IT FOR */}
      <section className="py-14 px-4 bg-rugby-blue text-white" data-id="element-43">
        <div className="max-w-5xl mx-auto" data-id="element-44">
          <h2 className="font-display text-4xl md:text-6xl text-center mb-10 uppercase" data-id="element-45">
            ¿Para quién es este material?
          </h2>
          <div className="grid md:grid-cols-4 gap-8 text-center" data-id="element-46">
            {[{
            icon: <Users className="w-12 h-12 mx-auto mb-4" data-id="element-47" />,
            title: 'Entrenadores de Escuelitas'
          }, {
            icon: <GraduationCap className="w-12 h-12 mx-auto mb-4" data-id="element-48" />,
            title: 'Profesores de Ed. Física'
          }, {
            icon: <BookOpen className="w-12 h-12 mx-auto mb-4" data-id="element-49" />,
            title: 'Estudiantes del Profesorado'
          }, {
            icon: <HeartHandshake className="w-12 h-12 mx-auto mb-4" data-id="element-50" />,
            title: 'Padres Colaboradores'
          }].map((item, i) => <div key={i} className="bg-white/10 p-6 rounded-2xl backdrop-blur-sm" data-id="element-51">
                {item.icon}
                <h3 className="font-bold text-xl" data-id="element-52">{item.title}</h3>
              </div>)}
          </div>
        </div>
      </section>

      {/* BONUSES */}
      <section className="py-14 px-4 bg-navy-900 border-t border-navy-700" data-id="element-53">
        <div className="max-w-5xl mx-auto" data-id="element-54">
          <div className="text-center mb-10" data-id="element-55">
            <h2 className="font-display text-5xl md:text-6xl mb-4 uppercase text-yellow-400" data-id="element-56">
              BONOS DE REGALO POR HOY
            </h2>
            <p className="text-xl md:text-2xl font-medium" data-id="element-57">
              Llevate estos{' '}
              <span className="text-yellow-400 font-bold" data-id="element-58">5 bonos</span>{' '}
              completamente{' '}
              <span className="text-yellow-400 font-bold" data-id="element-59">GRATIS</span>{' '}
              comprando hoy.
            </p>
            <p className="text-sm md:text-base text-slate-400 mt-3 uppercase tracking-widest font-bold" data-id="element-60">
              Tocá cada bono para ver su contenido completo
            </p>
          </div>

          <BonusesAccordion data-id="element-61" />

          <div className="text-center mt-10" data-id="element-62">
            <CTAButton text="QUIERO LOS BONOS GRATIS" data-id="element-63" />
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-14 px-4 bg-navy-900" data-id="element-64">
        <div className="max-w-4xl mx-auto" data-id="element-65">
          <h2 className="font-display text-4xl md:text-5xl text-center mb-8 uppercase" data-id="element-66">
            Lo que vas a <span className="text-rugby-blue" data-id="element-67">lograr</span>
          </h2>
          <div className="space-y-6" data-id="element-68">
            {['Ahorrar horas de planificación semanal teniendo todo resuelto.', 'Mantener a los chicos 100% motivados y enfocados durante toda la clase.', 'Elevar el nivel técnico de tu equipo con progresiones metodológicas correctas.', 'Proyectar una imagen mucho más profesional ante los padres y el club.', 'Disfrutar de dar clases sabiendo exactamente qué hacer en cada momento.'].map((benefit, i) => <div key={i} className="flex items-center gap-4 bg-navy-800 p-6 rounded-xl border border-navy-700" data-id="element-69">
                <CheckCircle2 className="w-8 h-8 text-green-500 flex-shrink-0" data-id="element-70" />
                <p className="text-xl font-medium" data-id="element-71">{benefit}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-14 px-4" data-id="element-72">
        <div className="max-w-6xl mx-auto" data-id="element-73">
          <h2 className="font-display text-4xl md:text-5xl text-center mb-10 uppercase" data-id="element-74">
            Lo que dicen nuestros{' '}
            <span className="text-rugby-blue" data-id="element-75">colegas</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-76">
            {[{
            name: 'Martín Gómez (32)',
            role: 'Entrenador escuelita',
            city: 'Rosario',
            text: 'Che, la verdad que me salvó la vida. Antes perdía horas pensando qué hacer con los más chicos. Ahora agarro el tomo de la semana y ya tengo la clase armada. ¡Un golazo!'
          }, {
            name: 'Carolina Pereyra (28)',
            role: 'Profesora de Ed. Física',
            city: 'La Plata',
            text: 'Excelente material. Muy bien estructurado y fácil de entender. Los juegos del Tomo 3 a los nenes les encantan y aprenden sin darse cuenta.'
          }, {
            name: 'Diego Fernández (45)',
            role: 'Coordinador rugby infantil',
            city: 'Tucumán',
            text: 'Compré el pack para todos los profes del club. Nos unificó la forma de enseñar y se nota muchísimo el progreso en las destrezas de los chicos este año.'
          }, {
            name: 'Lucía Sosa (24)',
            role: 'Estudiante de Ed. Física',
            city: 'Mendoza',
            text: 'Recién arranco a dar clases y me daba terror improvisar. Este material me dio una seguridad tremenda. Súper recomendable para los que recién empezamos.'
          }, {
            name: 'Federico Álvarez (38)',
            role: 'DT escuelita',
            city: 'Mar del Plata',
            text: 'Las planillas de evaluación son un lujo. Ahora puedo mostrarle a los padres exactamente en qué mejoró su hijo durante el año. Vale cada peso.'
          }].map((test, i) => <div key={i} className="bg-navy-800 p-8 rounded-2xl border border-navy-700 relative" data-id="element-77">
                <div className="flex gap-1 mb-4" data-id="element-78">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-5 h-5 text-yellow-400 fill-yellow-400" data-id="element-79" />)}
                </div>
                <p className="text-slate-300 italic mb-6 leading-relaxed" data-id="element-80">
                  "{test.text}"
                </p>
                <div data-id="element-81">
                  <p className="font-bold text-lg" data-id="element-82">{test.name}</p>
                  <p className="text-rugby-blue text-sm" data-id="element-83">{test.role}</p>
                  <p className="text-slate-500 text-sm" data-id="element-84">{test.city}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* PRICING / CHECKOUT */}
      <section id="comprar" className="py-14 px-4 relative overflow-hidden scroll-mt-24" data-id="element-85">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(37,99,235,0.1)_0%,transparent_70%)] pointer-events-none" data-id="element-86"></div>
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-87">
          <h2 className="font-display text-5xl md:text-6xl mb-8 uppercase" data-id="element-88">
            RESUMEN DE TU <span className="text-rugby-blue" data-id="element-89">COMPRA</span>
          </h2>

          <div className="bg-navy-900 p-8 md:p-12 rounded-3xl border-2 border-rugby-blue shadow-[0_0_50px_rgba(37,99,235,0.2)]" data-id="element-90">
            <ul className="text-left space-y-4 mb-8 text-lg" data-id="element-91">
              <li className="flex items-center gap-3" data-id="element-92">
                <CheckCircle2 className="w-6 h-6 text-rugby-blue" data-id="element-93" />{' '}
                <span data-id="element-94">Sistema Completo Rugby Infantil 360 (6 Tomos)</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-95">
                <CheckCircle2 className="w-6 h-6 text-yellow-400" data-id="element-96" />{' '}
                <span data-id="element-97">Bono 1: 50 Juegos Divertidos para Rugby Infantil</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-98">
                <CheckCircle2 className="w-6 h-6 text-yellow-400" data-id="element-99" />{' '}
                <span data-id="element-100">Bono 2: 30 Clases Armadas de Rugby Infantil</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-101">
                <CheckCircle2 className="w-6 h-6 text-yellow-400" data-id="element-102" />{' '}
                <span data-id="element-103">Bono 3: Circuitos y Estaciones para Entrenamientos</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-104">
                <CheckCircle2 className="w-6 h-6 text-yellow-400" data-id="element-105" />{' '}
                <span data-id="element-106">Bono 4: Planificación Anual de Rugby Infantil</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-107">
                <CheckCircle2 className="w-6 h-6 text-yellow-400" data-id="element-108" />{' '}
                <span data-id="element-109">Bono 5: Redes Sociales para Escuelitas y Clubes</span>
              </li>
            </ul>

            <div className="border-t border-navy-700 pt-8 mb-8" data-id="element-110">
              <p className="text-slate-400 text-xl mb-2" data-id="element-111">
                Precio Regular:{' '}
                <span className="line-through text-red-400" data-id="element-112">$49.990</span>
              </p>
              <p className="text-sm text-rugby-blue font-bold uppercase tracking-widest mb-2" data-id="element-113">
                OFERTA ESPECIAL POR HOY
              </p>
              <div className="font-display text-7xl md:text-8xl text-white mb-4" data-id="element-114">
                <span className="text-4xl align-top" data-id="element-115">$</span>19.990
              </div>
              <p className="text-sm text-slate-400" data-id="element-116">
                Pago único. Acceso de por vida al material digital.
              </p>
            </div>

            <CTAButton variant="buy" className="w-full" text="COMPRAR AHORA POR $19.990" data-id="element-117" />

            <div className="mt-6 flex items-center justify-center gap-4 text-sm text-slate-400" data-id="element-118">
              <ShieldCheck className="w-5 h-5" data-id="element-119" />
              <span data-id="element-120">Compra 100% Segura y Protegida</span>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-navy-900 py-12 px-4 border-t border-navy-700 text-center text-slate-500 text-sm" data-id="element-121">
        <div className="max-w-4xl mx-auto" data-id="element-122">
          <p className="mb-4" data-id="element-123">
            © {new Date().getFullYear()} Rugby Infantil 360. Todos los derechos
            reservados.
          </p>
          <p className="max-w-2xl mx-auto text-xs opacity-70" data-id="element-124">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio NO está respaldado por Facebook de ninguna
            manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>
        </div>
      </footer>
    </div>;
}